import { useEffect, useState } from 'react';
import { fetchOrderMarginPreview, type OrderMarginPreview, type OrderMarginPreviewPrice } from '../api/orderMarginPreviewApi';

/**
 * Live margin of the „Ceny sprzedaży” window: asks the server (debounced) for
 * the margin of the saved lines at the typed prices. `prices` null = nothing to
 * ask (a price is empty or the lines changed since the last save). Never
 * computes anything itself.
 */
export function useOrderMarginPreview(orderId: string | null, prices: OrderMarginPreviewPrice[] | null, enabled: boolean) {
  const request = JSON.stringify(prices);
  const [state, setState] = useState<{ orderId: string; request: string; preview: OrderMarginPreview | null; failed: boolean } | null>(null);
  useEffect(() => {
    if (!enabled || !orderId || !prices) return undefined;
    let active = true;
    const timer = setTimeout(() => {
      fetchOrderMarginPreview(orderId, JSON.parse(request)).then(
        (preview) => { if (active) setState({ orderId, request, preview, failed: false }); },
        () => { if (active) setState({ orderId, request, preview: null, failed: true }); },
      );
    }, 250);
    return () => { active = false; clearTimeout(timer); };
    // `prices` is tracked through its serialized form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, request, enabled]);
  const current = Boolean(state && state.orderId === orderId && state.request === request);
  const active = Boolean(enabled && orderId && prices);
  return {
    preview: active && current ? state?.preview ?? null : null,
    failed: active && current ? Boolean(state?.failed) : false,
    /** An answer for the typed prices is still on its way. */
    loading: active && !current,
  };
}
